/**
 * Shared error-message extraction (v225).
 *
 * The backend nests its human message under FastAPI's `detail` key, which can be
 * a plain string, an object with a `.message`, or (on 422s) a validation array.
 * Multiple surfaces mine this the same way — ErrorState pulls status+detail, and
 * the global mutation error handler needs the best human string for a toast — so
 * the logic lives here once instead of being re-implemented per call site.
 */

export interface ExtractedError {
  /** HTTP status when the error is an axios-style response, else undefined. */
  status?: number
  /** Best-available human message pulled from the payload, else undefined. */
  detail?: string
}

/**
 * Pulls HTTP status + best-available detail string out of an unknown error.
 * Handles axios errors, fetch-style Error objects, and plain strings.
 */
export function extractError(err: unknown): ExtractedError {
  if (!err) return {}
  if (typeof err === 'string') return { detail: err }
  if (typeof err !== 'object') return {}
  const e = err as Record<string, unknown>
  const response = e.response as Record<string, unknown> | undefined
  const status =
    typeof response?.status === 'number' ? (response.status as number) : undefined
  const data = response?.data as Record<string, unknown> | undefined
  const rawDetail = data?.detail
  let detail: string | undefined
  if (typeof rawDetail === 'string') {
    detail = rawDetail
  } else if (
    rawDetail &&
    typeof rawDetail === 'object' &&
    typeof (rawDetail as { message?: unknown }).message === 'string'
  ) {
    detail = (rawDetail as { message: string }).message
  } else if (typeof e.message === 'string') {
    detail = e.message
  }
  return { status, detail }
}

/**
 * A single human-readable message for an unknown error, suitable for a toast.
 * Falls back to a friendly default so a failed action is never fully silent.
 */
export function errorMessage(
  err: unknown,
  fallback = 'Something went wrong. Please try again.'
): string {
  const { detail } = extractError(err)
  return detail && detail.trim() ? detail : fallback
}

// ─── J5-402: typed quota / plan-gate errors (v227) ───────────────────────────
//
// The backend answers every quota- or tier-gated route with a STANDARD 402
// payload under FastAPI's `detail` key, discriminated on `detail.error`:
//
//   'plan_limit_exceeded' — a per-period quota (briefings_per_month,
//     dd_reports_per_month, …) or a row-count cap (max_alert_rules,
//     max_team_members, …) is used up. Carries feature / current_plan /
//     current_limit / current_usage (+ retry_after_seconds on time-window
//     quotas) and, on count-cap routes, the core/plans.build_upgrade_cta
//     fragment (upgrade_plan / upgrade_display_name / contact_sales /
//     contact_url).
//
//   'plan_required' — the whole feature needs a higher tier
//     (deps.require_*_subscription). Carries current_plan + upgrade_plan +
//     upgrade_display_name (+ feature_slug on routers that enrich it).
//
// parseQuotaExceededPayload() maps that wire shape into a camelCase
// QuotaExceededInfo; the api.ts response interceptor wraps a matching 402 in
// a typed QuotaExceededError. A 402 whose body does NOT match the standard
// shape parses to null on purpose, so surfaces that should never see a 402
// keep their generic error state instead of inventing an upgrade CTA.

export type QuotaExceededKind = 'plan_limit_exceeded' | 'plan_required'

export interface QuotaExceededInfo {
  kind: QuotaExceededKind
  /** Backend's human sentence, e.g. "Monthly briefing quota of 15 reached." */
  message?: string
  /** Machine feature key, e.g. "briefings_per_month" / "max_alert_rules". */
  feature?: string
  /** Router-level feature slug on enriched plan_required payloads. */
  featureSlug?: string
  /** Normalized tier name; the backend already maps NULL tier → "free". */
  currentPlan: string
  currentLimit?: number | null
  currentUsage?: number | null
  /** Seconds until a time-window quota resets (Retry-After parity). */
  retryAfterSeconds?: number | null
  upgradePlan?: string | null
  upgradeDisplayName?: string | null
  /** True when there is no next tier (Scale+): render contact-sales. */
  contactSales?: boolean
  contactUrl?: string | null
}

function numberOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/**
 * Parse an axios `response.data` body into QuotaExceededInfo, or null when it
 * is not the backend's standard 402 quota/plan-gate shape.
 */
export function parseQuotaExceededPayload(data: unknown): QuotaExceededInfo | null {
  if (!data || typeof data !== 'object') return null
  const detail = (data as { detail?: unknown }).detail
  if (!detail || typeof detail !== 'object') return null
  const d = detail as Record<string, unknown>
  const kind = d.error
  if (kind !== 'plan_limit_exceeded' && kind !== 'plan_required') return null
  return {
    kind,
    message: typeof d.message === 'string' ? d.message : undefined,
    feature: typeof d.feature === 'string' ? d.feature : undefined,
    featureSlug: typeof d.feature_slug === 'string' ? d.feature_slug : undefined,
    currentPlan:
      typeof d.current_plan === 'string' && d.current_plan ? d.current_plan : 'free',
    currentLimit: numberOrNull(d.current_limit),
    currentUsage: numberOrNull(d.current_usage),
    retryAfterSeconds: numberOrNull(d.retry_after_seconds),
    upgradePlan: typeof d.upgrade_plan === 'string' ? d.upgrade_plan : null,
    upgradeDisplayName:
      typeof d.upgrade_display_name === 'string' ? d.upgrade_display_name : null,
    contactSales: d.contact_sales === true,
    contactUrl: typeof d.contact_url === 'string' ? d.contact_url : null,
  }
}

/**
 * Typed rejection the api.ts interceptor substitutes for a recognized 402.
 * Keeps the original axios `response`/`config` so every existing consumer
 * (extractError, `err?.response?.data?.detail` mining, status checks) works
 * unchanged on the wrapped error.
 */
export class QuotaExceededError extends Error {
  readonly quota: QuotaExceededInfo
  readonly response?: unknown
  readonly config?: unknown

  constructor(quota: QuotaExceededInfo, original?: unknown) {
    super(quota.message || 'Your plan limit for this feature has been reached.')
    this.name = 'QuotaExceededError'
    this.quota = quota
    if (original && typeof original === 'object') {
      const o = original as { response?: unknown; config?: unknown }
      this.response = o.response
      this.config = o.config
    }
  }
}

/**
 * Best-effort extraction of quota info from an unknown error: a
 * QuotaExceededError (or a structurally-equivalent copy that lost its
 * prototype), else a raw axios-style 402 whose body matches the standard
 * shape. Returns null otherwise — callers then fall back to generic copy.
 */
export function extractQuotaExceeded(err: unknown): QuotaExceededInfo | null {
  if (!err || typeof err !== 'object') return null
  if (err instanceof QuotaExceededError) return err.quota
  const e = err as { name?: unknown; quota?: unknown; response?: { data?: unknown } }
  if (e.name === 'QuotaExceededError' && e.quota && typeof e.quota === 'object') {
    return e.quota as QuotaExceededInfo
  }
  const { status } = extractError(err)
  if (status !== 402) return null
  return parseQuotaExceededPayload(e.response?.data)
}
